import type { DesignConfiguration } from '../../lib/api/design';
import { createLegacyColorPalette, defaultBrandingTokens, defaultDesignTokens } from '../tokens';

export type ThemeCSSVariableName = `--${string}`;
export type ThemeCSSVariables = Record<ThemeCSSVariableName, string>;

const REQUIRED_COLOR_FIELDS = [
  'primary_gradient_start',
  'primary_gradient_end',
  'secondary_gradient_start',
  'secondary_gradient_end',
  'primary_button_gradient_start',
  'primary_button_gradient_end',
  'primary_button_bg',
  'primary_button_hover',
  'secondary_button_bg',
  'secondary_button_hover',
  'primary_text',
  'secondary_text',
  'accent_text',
  'card_background',
  'border_color',
] as const;

const OPTIONAL_COLOR_FIELDS = [
  'gradient_accent_start',
  'success',
  'warning',
  'error',
  'info',
  'chart_1',
  'chart_2',
  'chart_3',
  'chart_4',
  'chart_5',
  'chart_6',
  'chart_7',
  'chart_8',
  'rarity_common',
  'rarity_uncommon',
  'rarity_rare',
  'rarity_epic',
  'rarity_legendary',
  'swipe_like',
  'swipe_superlike',
  'swipe_dislike',
  'nav_icon_active',
  'nav_badge_bg',
  'action_like',
  'action_like_active',
  'action_comment',
  'action_view',
  'glass_dropdown_bg',
  'glass_dropdown_border',
  'glass_dropdown_highlight',
] as const;

const REQUIRED_TYPOGRAPHY_STRING_FIELDS = ['primary_font', 'secondary_font', 'accent_font'] as const;

const REQUIRED_TYPOGRAPHY_NUMBER_FIELDS = [
  'hero_title_size',
  'page_title_size',
  'section_title_size',
  'body_text_size',
  'small_text_size',
  'title_weight',
  'body_weight',
] as const;

const REQUIRED_LAYOUT_STRING_FIELDS = [
  'max_container_width',
  'content_padding',
  'section_spacing',
  'button_radius',
  'card_radius',
  'transition_duration',
] as const;

const REQUIRED_BRANDING_FIELDS = [
  'hero_title',
  'hero_subtitle',
  'primary_cta_text',
  'secondary_cta_text',
  'site_title',
  'meta_description',
] as const;

const EXTENSION_STRING_FIELDS = {
  spacing: ['xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl'],
  radius: ['none', 'sm', 'md', 'lg', 'xl', 'xxl', 'full', 'button', 'card', 'input', 'modal', 'avatar'],
  shadows: ['sm', 'md', 'lg', 'xl', 'card', 'dropdown', 'modal', 'glow', 'glow_primary', 'inner'],
  animation: ['duration_fast', 'duration_normal', 'duration_slow', 'easing_default', 'easing_in', 'easing_out'],
  components: [
    'button_height_sm',
    'button_height_md',
    'button_height_lg',
    'button_padding_x',
    'input_height',
    'input_padding_x',
    'input_border_width',
    'card_padding',
    'card_gap',
    'nav_height_desktop',
    'nav_height_mobile',
  ],
} as const;

const EXTENSION_NUMBER_FIELDS = {
  animation: ['hover_scale', 'press_scale'],
  components: ['button_font_weight'],
} as const;

const EXTENSION_KEYS = ['spacing', 'radius', 'shadows', 'animation', 'components'] as const;

const ROOT_FONT_SIZE_PX = 16;
const SPACING_GRID_PX = 4;
const MINIMUM_INTERACTION_SIZE_PX = 44;
const COMPONENT_INTERACTION_FIELDS = [
  'button_height_sm',
  'button_height_md',
  'button_height_lg',
  'input_height',
  'nav_height_desktop',
  'nav_height_mobile',
] as const;
const COMPONENT_SPACING_FIELDS = ['button_padding_x', 'input_padding_x', 'card_padding', 'card_gap'] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function requireRecord(source: Record<string, unknown>, key: string, path: string): Record<string, unknown> {
  const value = source[key];
  if (!isRecord(value)) {
    throw new Error(`Invalid design configuration: ${path}.${key} must be an object`);
  }
  return value;
}

function requireString(source: Record<string, unknown>, key: string, path: string): void {
  const value = source[key];
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`Invalid design configuration: ${path}.${key} must be a non-empty string`);
  }
}

function requireFiniteNumber(source: Record<string, unknown>, key: string, path: string): void {
  const value = source[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`Invalid design configuration: ${path}.${key} must be a finite number`);
  }
}

function requireBoolean(source: Record<string, unknown>, key: string, path: string): void {
  if (typeof source[key] !== 'boolean') {
    throw new Error(`Invalid design configuration: ${path}.${key} must be a boolean`);
  }
}

function validateOptionalString(source: Record<string, unknown>, key: string, path: string): void {
  const value = source[key];
  if (value !== undefined && value !== null && (typeof value !== 'string' || value.trim() === '')) {
    throw new Error(`Invalid design configuration: ${path}.${key} must be a non-empty string when provided`);
  }
}

function validateOptionalNumber(source: Record<string, unknown>, key: string, path: string): void {
  const value = source[key];
  if (value !== undefined && value !== null && (typeof value !== 'number' || !Number.isFinite(value))) {
    throw new Error(`Invalid design configuration: ${path}.${key} must be a finite number when provided`);
  }
}

function cssLengthToPixels(value: string, path: string): number {
  const match = /^(\d+(?:\.\d+)?)(px|rem)$/.exec(value.trim());
  if (match === null) {
    throw new Error(`Invalid design configuration: ${path} must use a non-negative px or rem length`);
  }

  const amount = Number.parseFloat(match[1] ?? '');
  return match[2] === 'rem' ? amount * ROOT_FONT_SIZE_PX : amount;
}

function validateGridLength(source: Record<string, unknown>, key: string, path: string): void {
  const value = source[key];
  if (value === undefined || value === null) return;
  if (typeof value !== 'string') return;

  const pixels = cssLengthToPixels(value, `${path}.${key}`);
  if (Math.abs(pixels % SPACING_GRID_PX) > Number.EPSILON) {
    throw new Error(`Invalid design configuration: ${path}.${key} must align to the 4px spacing grid`);
  }
}

function validateMinimumInteractionLength(source: Record<string, unknown>, key: string, path: string): void {
  const value = source[key];
  if (value === undefined || value === null) return;
  if (typeof value !== 'string') return;

  if (cssLengthToPixels(value, `${path}.${key}`) < MINIMUM_INTERACTION_SIZE_PX) {
    throw new Error(`Invalid design configuration: ${path}.${key} must be at least 44px`);
  }
}

function validateAscendingLengths(source: Record<string, unknown>, keys: readonly string[], path: string): void {
  let previous: { key: string; pixels: number } | undefined;

  for (const key of keys) {
    const value = source[key];
    if (typeof value !== 'string') continue;
    const current = { key, pixels: cssLengthToPixels(value, `${path}.${key}`) };
    if (previous !== undefined && current.pixels < previous.pixels) {
      throw new Error(
        `Invalid design configuration: ${path}.${current.key} must not be smaller than ${path}.${previous.key}`
      );
    }
    previous = current;
  }
}

function validateOptionalBoolean(source: Record<string, unknown>, key: string, path: string): void {
  const value = source[key];
  if (value !== undefined && value !== null && typeof value !== 'boolean') {
    throw new Error(`Invalid design configuration: ${path}.${key} must be a boolean when provided`);
  }
}

function validateOptionalExtension(layout: Record<string, unknown>, key: keyof typeof EXTENSION_STRING_FIELDS): void {
  const extension = layout[key];
  if (extension === undefined || extension === null) return;
  if (!isRecord(extension)) {
    throw new Error(`Invalid design configuration: layout.${key} must be an object when provided`);
  }

  for (const field of EXTENSION_STRING_FIELDS[key]) {
    validateOptionalString(extension, field, `layout.${key}`);
  }

  if (key === 'spacing') {
    for (const field of EXTENSION_STRING_FIELDS.spacing) {
      validateGridLength(extension, field, 'layout.spacing');
    }
    validateAscendingLengths(extension, EXTENSION_STRING_FIELDS.spacing, 'layout.spacing');
  }

  if (key === 'components') {
    for (const field of COMPONENT_INTERACTION_FIELDS) {
      validateMinimumInteractionLength(extension, field, 'layout.components');
    }
    for (const field of COMPONENT_SPACING_FIELDS) {
      validateGridLength(extension, field, 'layout.components');
    }
    validateAscendingLengths(
      extension,
      ['button_height_sm', 'button_height_md', 'button_height_lg'],
      'layout.components'
    );
  }

  if (key === 'animation' || key === 'components') {
    for (const field of EXTENSION_NUMBER_FIELDS[key]) {
      validateOptionalNumber(extension, field, `layout.${key}`);
    }
  }
}

export function assertDesignConfiguration(value: unknown): asserts value is DesignConfiguration {
  if (!isRecord(value)) {
    throw new Error('Invalid design configuration: response must be an object');
  }

  requireBoolean(value, 'enabled', 'design');
  requireString(value, 'theme_name', 'design');

  const colors = requireRecord(value, 'colors', 'design');
  for (const field of REQUIRED_COLOR_FIELDS) requireString(colors, field, 'colors');
  for (const field of OPTIONAL_COLOR_FIELDS) validateOptionalString(colors, field, 'colors');

  const typography = requireRecord(value, 'typography', 'design');
  for (const field of REQUIRED_TYPOGRAPHY_STRING_FIELDS) requireString(typography, field, 'typography');
  for (const field of REQUIRED_TYPOGRAPHY_NUMBER_FIELDS) requireFiniteNumber(typography, field, 'typography');

  const layout = requireRecord(value, 'layout', 'design');
  for (const field of REQUIRED_LAYOUT_STRING_FIELDS) requireString(layout, field, 'layout');
  requireFiniteNumber(layout, 'hover_scale', 'layout');
  for (const extension of EXTENSION_KEYS) validateOptionalExtension(layout, extension);

  const branding = requireRecord(value, 'branding', 'design');
  for (const field of REQUIRED_BRANDING_FIELDS) requireString(branding, field, 'branding');
  for (const field of ['logo_url', 'mobile_logo_url', 'favicon_url'] as const) {
    validateOptionalString(branding, field, 'branding');
  }
  for (const field of ['show_site_name', 'robots_index', 'robots_follow'] as const) {
    validateOptionalBoolean(branding, field, 'branding');
  }

  const characterVisibility = requireRecord(value, 'character_visibility', 'design');
  requireBoolean(characterVisibility, 'show_public_only_before_login', 'character_visibility');
  validateOptionalBoolean(value, 'show_ai_disclaimer', 'design');
  validateOptionalBoolean(value, 'feature_voice_input_enabled', 'design');
  validateOptionalBoolean(value, 'feature_games_enabled', 'design');
}

export function parseDesignConfiguration(value: unknown): DesignConfiguration {
  assertDesignConfiguration(value);

  const defaultLayout = defaultDesignConfiguration.layout;
  return {
    ...value,
    layout: {
      ...value.layout,
      spacing: { ...defaultLayout.spacing, ...(value.layout.spacing ?? {}) },
      radius: { ...defaultLayout.radius, ...(value.layout.radius ?? {}) },
      shadows: { ...defaultLayout.shadows, ...(value.layout.shadows ?? {}) },
      animation: { ...defaultLayout.animation, ...(value.layout.animation ?? {}) },
      components: { ...defaultLayout.components, ...(value.layout.components ?? {}) },
      preset_id: value.layout.preset_id ?? defaultLayout.preset_id ?? 'modern',
    },
  };
}

function remNumber(value: string): number {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) throw new Error(`Invalid default typography token: ${value}`);
  return parsed;
}

export const defaultDesignConfiguration: DesignConfiguration = {
  enabled: true,
  theme_name: 'Purple Passion Premium',
  colors: createLegacyColorPalette(defaultDesignTokens.colors),
  typography: {
    primary_font: defaultDesignTokens.typography.fontFamily.primary,
    secondary_font: defaultDesignTokens.typography.fontFamily.secondary,
    accent_font: defaultDesignTokens.typography.fontFamily.accent,
    hero_title_size: remNumber(defaultDesignTokens.typography.fontSize['6xl']),
    page_title_size: remNumber(defaultDesignTokens.typography.fontSize['4xl']),
    section_title_size: remNumber(defaultDesignTokens.typography.fontSize['2xl']),
    body_text_size: remNumber(defaultDesignTokens.typography.fontSize.base),
    small_text_size: remNumber(defaultDesignTokens.typography.fontSize.sm),
    title_weight: defaultDesignTokens.typography.fontWeight.bold,
    body_weight: defaultDesignTokens.typography.fontWeight.normal,
  },
  layout: {
    max_container_width: '1200px',
    content_padding: defaultDesignTokens.spacing.xl,
    section_spacing: defaultDesignTokens.spacing['3xl'],
    button_radius: defaultDesignTokens.radius.button,
    card_radius: defaultDesignTokens.radius.card,
    transition_duration: defaultDesignTokens.animation.durationNormal,
    hover_scale: defaultDesignTokens.animation.hoverScale,
    spacing: {
      xs: defaultDesignTokens.spacing.xs,
      sm: defaultDesignTokens.spacing.sm,
      md: defaultDesignTokens.spacing.md,
      lg: defaultDesignTokens.spacing.lg,
      xl: defaultDesignTokens.spacing.xl,
      xxl: defaultDesignTokens.spacing['2xl'],
      xxxl: defaultDesignTokens.spacing['3xl'],
    },
    radius: {
      none: defaultDesignTokens.radius.none,
      sm: defaultDesignTokens.radius.sm,
      md: defaultDesignTokens.radius.md,
      lg: defaultDesignTokens.radius.lg,
      xl: defaultDesignTokens.radius.xl,
      xxl: defaultDesignTokens.radius['2xl'],
      full: defaultDesignTokens.radius.full,
      button: defaultDesignTokens.radius.button,
      card: defaultDesignTokens.radius.card,
      input: defaultDesignTokens.radius.input,
      modal: defaultDesignTokens.radius.modal,
      avatar: defaultDesignTokens.radius.avatar,
    },
    shadows: {
      sm: defaultDesignTokens.shadows.sm,
      md: defaultDesignTokens.shadows.md,
      lg: defaultDesignTokens.shadows.lg,
      xl: defaultDesignTokens.shadows.xl,
      card: defaultDesignTokens.shadows.card,
      dropdown: defaultDesignTokens.shadows.dropdown,
      modal: defaultDesignTokens.shadows.modal,
      glow: defaultDesignTokens.shadows.glow,
      glow_primary: defaultDesignTokens.shadows.glowPrimary,
      inner: defaultDesignTokens.shadows.inner,
    },
    animation: {
      duration_fast: defaultDesignTokens.animation.durationFast,
      duration_normal: defaultDesignTokens.animation.durationNormal,
      duration_slow: defaultDesignTokens.animation.durationSlow,
      easing_default: defaultDesignTokens.animation.easingDefault,
      easing_in: defaultDesignTokens.animation.easingIn,
      easing_out: defaultDesignTokens.animation.easingOut,
      hover_scale: defaultDesignTokens.animation.hoverScale,
      press_scale: defaultDesignTokens.animation.pressScale,
    },
    components: {
      button_height_sm: defaultDesignTokens.components.button.heightSm,
      button_height_md: defaultDesignTokens.components.button.heightMd,
      button_height_lg: defaultDesignTokens.components.button.heightLg,
      button_padding_x: defaultDesignTokens.components.button.paddingX,
      button_font_weight: defaultDesignTokens.components.button.fontWeight,
      input_height: defaultDesignTokens.components.input.height,
      input_padding_x: defaultDesignTokens.components.input.paddingX,
      input_border_width: defaultDesignTokens.components.input.borderWidth,
      card_padding: defaultDesignTokens.components.card.padding,
      card_gap: defaultDesignTokens.components.card.gap,
      nav_height_desktop: defaultDesignTokens.components.navigation.heightDesktop,
      nav_height_mobile: defaultDesignTokens.components.navigation.heightMobile,
    },
  },
  branding: {
    hero_title: defaultBrandingTokens.heroTitle,
    hero_subtitle: defaultBrandingTokens.heroSubtitle,
    primary_cta_text: defaultBrandingTokens.primaryCTA,
    secondary_cta_text: defaultBrandingTokens.secondaryCTA,
    site_title: defaultBrandingTokens.siteTitle,
    meta_description: defaultBrandingTokens.metaDescription,
  },
  character_visibility: {
    show_public_only_before_login: false,
  },
  // Hide disclosures until the public operator setting has loaded. This
  // prevents a visible flash and preserves the explicit admin decision.
  show_ai_disclaimer: false,
  // Fail-open default so a not-yet-loaded design config never hides the games
  // button on installs where the (server-side, also fail-open) admin switch
  // is unset — matches production behavior for the already-shipped feature.
  feature_games_enabled: true,
};

function hexToRGB(hex: string): [number, number, number] {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (match === null) throw new Error(`Invalid published theme color: ${hex}`);
  const value = match[1];
  if (value === undefined) throw new Error(`Invalid published theme color: ${hex}`);
  return [
    Number.parseInt(value.slice(0, 2), 16),
    Number.parseInt(value.slice(2, 4), 16),
    Number.parseInt(value.slice(4, 6), 16),
  ];
}

function toRGBString(hex: string): string {
  return hexToRGB(hex).join(', ');
}

function rgbToHSLString(rgb: [number, number, number]): string {
  const red = rgb[0] / 255;
  const green = rgb[1] / 255;
  const blue = rgb[2] / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;
  let hue = 0;
  let saturation = 0;

  if (max !== min) {
    const delta = max - min;
    saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    if (max === red) hue = ((green - blue) / delta + (green < blue ? 6 : 0)) / 6;
    if (max === green) hue = ((blue - red) / delta + 2) / 6;
    if (max === blue) hue = ((red - green) / delta + 4) / 6;
  }

  return `${Math.round(hue * 360)} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%`;
}

function toHSLString(hex: string): string {
  return rgbToHSLString(hexToRGB(hex));
}

function relativeLuminance(rgb: [number, number, number]): number {
  const channels = rgb.map((value) => {
    const scaled = value / 255;
    return scaled <= 0.04045 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * (channels[0] ?? 0) + 0.7152 * (channels[1] ?? 0) + 0.0722 * (channels[2] ?? 0);
}

function accessibleButtonForeground(background: string): string {
  const luminance = relativeLuminance(hexToRGB(background));
  const whiteContrast = 1.05 / (luminance + 0.05);
  const blackContrast = (luminance + 0.05) / 0.05;
  return whiteContrast >= blackContrast ? '#FFFFFF' : '#000000';
}

function toHSLAChannels(color: string): { hsl: string; alpha: string } {
  if (/^#[0-9a-f]{6}$/i.test(color)) {
    return { hsl: toHSLString(color), alpha: '1' };
  }

  const rgbaMatch = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.exec(color);
  if (rgbaMatch === null) throw new Error(`Invalid published theme color: ${color}`);

  const channels = rgbaMatch.slice(1, 4).map(Number);
  if (channels.some((channel) => channel < 0 || channel > 255)) {
    throw new Error(`Invalid published theme color: ${color}`);
  }
  const red = channels[0];
  const green = channels[1];
  const blue = channels[2];
  if (red === undefined || green === undefined || blue === undefined) {
    throw new Error(`Invalid published theme color: ${color}`);
  }

  const alpha = rgbaMatch[4] ?? '1';
  return { hsl: rgbToHSLString([red, green, blue]), alpha };
}

function addVariable(
  variables: ThemeCSSVariables,
  name: ThemeCSSVariableName,
  value: string | number | null | undefined
): void {
  if (value === undefined || value === null) return;
  const serialized = String(value).trim();
  if (serialized === '') throw new Error(`Invalid published theme value for ${name}: value is empty`);
  if (/[;{}]/.test(serialized)) throw new Error(`Invalid published theme value for ${name}: unsafe CSS syntax`);

  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (new RegExp(`var\\(\\s*${escapedName}(?:\\s*[,\\)])`, 'i').test(serialized)) {
    throw new Error(`Invalid published theme value for ${name}: self-referential CSS variable`);
  }
  variables[name] = serialized;
}

export function createThemeCSSVariables(config: DesignConfiguration): ThemeCSSVariables {
  assertDesignConfiguration(config);
  const variables: ThemeCSSVariables = {};
  const add = (name: ThemeCSSVariableName, value: string | number | null | undefined): void => {
    addVariable(variables, name, value);
  };

  const colors = config.colors;
  add('--color-primary-gradient-start', colors.primary_gradient_start);
  add('--color-primary-gradient-end', colors.primary_gradient_end);
  add('--color-secondary-gradient-start', colors.secondary_gradient_start);
  add('--color-secondary-gradient-end', colors.secondary_gradient_end);
  add('--color-primary-button-gradient-start', colors.primary_button_gradient_start);
  add('--color-primary-button-gradient-end', colors.primary_button_gradient_end);
  add('--color-gradient-accent-start', colors.gradient_accent_start);
  add('--color-primary-button', colors.primary_button_bg);
  add('--color-primary-button-hover', colors.primary_button_hover);
  add('--color-secondary-button', colors.secondary_button_bg);
  add('--color-secondary-button-hover', colors.secondary_button_hover);
  add('--color-primary-text', colors.primary_text);
  add('--color-secondary-text', colors.secondary_text);
  add('--color-accent-text', colors.accent_text);
  add('--color-card-background', colors.card_background);
  add('--color-border', colors.border_color);
  const cardSurface = toHSLAChannels(colors.card_background);
  const borderSurface = toHSLAChannels(colors.border_color);
  add('--theme-card-hsl', cardSurface.hsl);
  add('--theme-card-alpha', cardSurface.alpha);
  add('--theme-border-hsl', borderSurface.hsl);
  add('--theme-border-alpha', borderSurface.alpha);
  add('--foreground', toHSLString(colors.primary_text));
  add('--card-foreground', toHSLString(colors.primary_text));
  add('--muted-foreground', toHSLString(colors.secondary_text));
  add('--primary', toHSLString(colors.primary_button_bg));
  add('--primary-foreground', toHSLString(accessibleButtonForeground(colors.primary_button_bg)));
  add('--secondary', toHSLString(colors.secondary_button_bg));
  add('--secondary-foreground', toHSLString(colors.primary_text));
  add('--accent', toHSLString(colors.accent_text));
  add('--accent-foreground', toHSLString(colors.primary_text));
  add('--ring', toHSLString(colors.primary_button_bg));
  add('--brand-primary', toHSLString(colors.primary_button_bg));
  add('--brand-primary-hex', colors.primary_button_bg);
  add('--brand-secondary', toHSLString(colors.primary_gradient_start));
  add('--brand-secondary-hex', colors.primary_gradient_start);
  add('--color-primary-gradient-start-rgb', toRGBString(colors.primary_gradient_start));
  add('--color-primary-gradient-end-rgb', toRGBString(colors.primary_gradient_end));
  add('--color-primary-button-rgb', toRGBString(colors.primary_button_bg));
  add('--color-accent-text-rgb', toRGBString(colors.accent_text));
  add('--brand-primary-rgb', toRGBString(colors.primary_button_bg));
  add('--brand-secondary-rgb', toRGBString(colors.primary_gradient_start));

  const colorVariables: Array<[ThemeCSSVariableName, string | null | undefined]> = [
    ['--color-success', colors.success],
    ['--color-warning', colors.warning],
    ['--color-error', colors.error],
    ['--color-info', colors.info],
    ['--color-chart-1', colors.chart_1],
    ['--color-chart-2', colors.chart_2],
    ['--color-chart-3', colors.chart_3],
    ['--color-chart-4', colors.chart_4],
    ['--color-chart-5', colors.chart_5],
    ['--color-chart-6', colors.chart_6],
    ['--color-chart-7', colors.chart_7],
    ['--color-chart-8', colors.chart_8],
    ['--color-rarity-common', colors.rarity_common],
    ['--color-rarity-uncommon', colors.rarity_uncommon],
    ['--color-rarity-rare', colors.rarity_rare],
    ['--color-rarity-epic', colors.rarity_epic],
    ['--color-rarity-legendary', colors.rarity_legendary],
    ['--color-swipe-like', colors.swipe_like],
    ['--color-swipe-superlike', colors.swipe_superlike],
    ['--color-swipe-dislike', colors.swipe_dislike],
    ['--color-nav-icon-active', colors.nav_icon_active],
    ['--color-nav-badge-bg', colors.nav_badge_bg],
    ['--color-action-like', colors.action_like],
    ['--color-action-like-hover', colors.action_like],
    ['--color-action-like-active', colors.action_like_active],
    ['--color-action-comment', colors.action_comment],
    ['--color-action-comment-hover', colors.action_comment],
    ['--color-action-view', colors.action_view],
    ['--color-action-view-hover', colors.action_view],
    ['--glass-dropdown-bg', colors.glass_dropdown_bg],
    ['--glass-dropdown-border', colors.glass_dropdown_border],
    ['--glass-dropdown-highlight', colors.glass_dropdown_highlight],
  ];
  for (const [name, value] of colorVariables) add(name, value);

  const typography = config.typography;
  add('--font-primary', typography.primary_font);
  add('--font-secondary', typography.secondary_font);
  add('--font-accent', typography.accent_font);
  add('--font-size-hero', `${typography.hero_title_size}rem`);
  add('--font-size-page-title', `${typography.page_title_size}rem`);
  add('--font-size-section-title', `${typography.section_title_size}rem`);
  add('--font-size-body', `${typography.body_text_size}rem`);
  add('--font-size-small', `${typography.small_text_size}rem`);
  add('--font-weight-title', typography.title_weight);
  add('--font-weight-body', typography.body_weight);

  const layout = config.layout;
  add('--layout-max-width', layout.max_container_width);
  add('--layout-padding', layout.content_padding);
  add('--layout-section-spacing', layout.section_spacing);
  add('--border-radius-button', layout.button_radius);
  add('--border-radius-card', layout.card_radius);
  add('--radius', layout.card_radius);
  add('--transition-duration', layout.transition_duration);
  add('--hover-scale', layout.hover_scale);

  const spacing = layout.spacing;
  if (spacing) {
    add('--spacing-xs', spacing.xs);
    add('--spacing-sm', spacing.sm);
    add('--spacing-md', spacing.md);
    add('--spacing-lg', spacing.lg);
    add('--spacing-xl', spacing.xl);
    add('--spacing-2xl', spacing.xxl);
    add('--spacing-3xl', spacing.xxxl);
  }

  const radius = layout.radius;
  if (radius) {
    add('--radius-none', radius.none);
    add('--radius-sm', radius.sm);
    add('--radius-md', radius.md);
    add('--radius-lg', radius.lg);
    add('--radius-xl', radius.xl);
    add('--radius-2xl', radius.xxl);
    add('--radius-full', radius.full);
    add('--radius-button', radius.button);
    add('--radius-card', radius.card);
    add('--radius-input', radius.input);
    add('--radius-modal', radius.modal);
    add('--radius-avatar', radius.avatar);
  }

  const shadows = layout.shadows;
  if (shadows) {
    add('--shadow-sm', shadows.sm);
    add('--shadow-md', shadows.md);
    add('--shadow-lg', shadows.lg);
    add('--shadow-xl', shadows.xl);
    add('--shadow-card', shadows.card);
    add('--shadow-dropdown', shadows.dropdown);
    add('--shadow-modal', shadows.modal);
    add('--shadow-glow', shadows.glow);
    add('--shadow-glow-primary', shadows.glow_primary);
    add('--shadow-inner', shadows.inner);
  }

  const animation = layout.animation;
  if (animation) {
    add('--animation-duration-fast', animation.duration_fast);
    add('--animation-duration-normal', animation.duration_normal);
    add('--animation-duration-slow', animation.duration_slow);
    add('--animation-easing', animation.easing_default);
    add('--animation-easing-in', animation.easing_in);
    add('--animation-easing-out', animation.easing_out);
    add('--animation-hover-scale', animation.hover_scale);
    add('--animation-press-scale', animation.press_scale);
  }

  const components = layout.components;
  if (components) {
    add('--component-button-height-sm', components.button_height_sm);
    add('--component-button-height-md', components.button_height_md);
    add('--component-button-height-lg', components.button_height_lg);
    add('--component-button-padding-x', components.button_padding_x);
    add('--component-button-font-weight', components.button_font_weight);
    add('--component-input-height', components.input_height);
    add('--component-input-padding-x', components.input_padding_x);
    add('--component-input-border-width', components.input_border_width);
    add('--component-card-padding', components.card_padding);
    add('--component-card-gap', components.card_gap);
    add('--nav-height-desktop', components.nav_height_desktop);
    add('--nav-height-mobile', components.nav_height_mobile);
  }

  return variables;
}
